import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, SaveIcon, FlaskConicalIcon, CheckCircle2Icon, PuzzleIcon, BookOpenIcon } from 'lucide-react';
import { useScreenInit } from '../useScreenInit';
import { useAgentStore, AgentDraft } from '../stores/agentStore';
import { useSkillStore } from '../stores/skillStore';
import { useKnowledgeStore } from '../stores/knowledgeStore';
import { useModelStore } from '../stores/modelStore';
import { useUiStore } from '../stores/uiStore';
import { Agent, ToolApprovalMode } from '../types';
import { PageContainer } from '../components/common/page';
import { Card, Button, Badge, Input, Select, Label, Textarea } from '../components/common/ui';
import { AgentChatDialog } from '../components/chat/AgentChatDialog';
const PROMPT_TEMPLATES = [{
  label: '通用助手',
  text: '你是一个乐于助人的智能助手，回答准确、简洁。'
}, {
  label: 'DevOps 工程师',
  text: '你是一名资深 DevOps 工程师，擅长 Linux 运维与故障排查。回答简洁、可执行。'
}, {
  label: '代码专家',
  text: '你是一名全栈工程师，编写高质量、可维护的代码，并解释关键决策。'
}];
export function AgentCreatePage() {
  useScreenInit();
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const agents = useAgentStore(s => s.agents);
  const {
    createAgent,
    updateAgent,
    activateAgent
  } = useAgentStore();
  const skills = useSkillStore(s => s.skills.filter(sk => sk.status === 'enabled'));
  const knowledgeBases = useKnowledgeStore(s => s.knowledgeBases.filter(kb => kb.status === 'ready'));
  const models = useModelStore(s => s.models.filter(m => m.enabled));
  const pushToast = useUiStore(s => s.pushToast);
  const editing = useMemo(() => agents.find(a => a.agentId === id), [agents, id]);
  const [draft, setDraft] = useState<AgentDraft>(() => ({
    name: editing?.name || '',
    description: editing?.description || '',
    systemPrompt: editing?.systemPrompt || '',
    skillIds: editing?.skillIds || [],
    knowledgeBaseIds: editing?.knowledgeBaseIds || [],
    defaultModelId: editing?.defaultModelId || models[0]?.modelId || '',
    tags: editing?.tags || [],
    retrievalConfig: editing?.retrievalConfig || {
      topK: 5,
      scoreThreshold: 0.7,
      showCitations: true
    },
    runtimeConfig: editing?.runtimeConfig || {
      toolApprovalMode: 'auto',
      maxExecutionSeconds: 300
    }
  }));
  const [tagInput, setTagInput] = useState('');
  const [savedAgent, setSavedAgent] = useState<Agent | null>(null);
  const [testOpen, setTestOpen] = useState(false);
  const set = (patch: Partial<AgentDraft>) => setDraft(d => ({
    ...d,
    ...patch
  }));
  const valid = draft.name.trim() && draft.description.trim() && draft.systemPrompt.trim() && draft.defaultModelId;
  const skillsResolver = (ids: string[]): Agent['skills'] => useSkillStore.getState().skills.filter(s => ids.includes(s.skillId)).map(s => ({
    skillId: s.skillId,
    name: s.name,
    description: s.description,
    version: s.version
  }));
  const knowledgeBaseResolver = (ids: string[]): Agent['knowledgeBases'] => useKnowledgeStore.getState().knowledgeBases.filter(kb => ids.includes(kb.knowledgeBaseId)).map(kb => ({
    knowledgeBaseId: kb.knowledgeBaseId,
    name: kb.name
  }));
  const handleSave = (): Agent | null => {
    if (!valid) {
      pushToast('error', '请填写所有必填字段');
      return null;
    }
    if (editing) {
      updateAgent(editing.agentId, draft, skillsResolver, knowledgeBaseResolver);
      pushToast('success', 'Agent 已保存');
      const updated = useAgentStore.getState().agents.find(a => a.agentId === editing.agentId)!;
      setSavedAgent(updated);
      return updated;
    } else {
      const newId = createAgent(draft, skillsResolver, knowledgeBaseResolver);
      pushToast('success', 'Agent 已创建');
      const created = useAgentStore.getState().agents.find(a => a.agentId === newId)!;
      setSavedAgent(created);
      navigate(`/agents/${newId}/edit`, {
        replace: true
      });
      return created;
    }
  };
  const handleSaveAndTest = () => {
    const a = handleSave();
    if (a) setTestOpen(true);
  };
  const toggleSkill = (skillId: string) => {
    set({
      skillIds: draft.skillIds.includes(skillId) ? draft.skillIds.filter(x => x !== skillId) : [...draft.skillIds, skillId]
    });
  };
  const toggleKb = (kbId: string) => {
    set({
      knowledgeBaseIds: draft.knowledgeBaseIds.includes(kbId) ? draft.knowledgeBaseIds.filter(x => x !== kbId) : [...draft.knowledgeBaseIds, kbId]
    });
  };
  const addTag = () => {
    const t = tagInput.trim();
    if (t && !draft.tags.includes(t)) set({
      tags: [...draft.tags, t]
    });
    setTagInput('');
  };
  return <PageContainer data-id="element-308">
      <button onClick={() => navigate('/agents')} className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200" data-id="element-309">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-310" /> 返回 Agent 中心
      </button>
      <div className="mb-6 flex items-center justify-between" data-id="element-311">
        <h2 className="text-xl font-semibold text-slate-100" data-id="element-312">
          {editing ? '编辑 Agent' : '创建 Agent'}
        </h2>
        <div className="flex gap-2" data-id="element-313">
          <Button variant="outline" onClick={handleSave} disabled={!valid} data-id="element-314">
            <SaveIcon className="h-4 w-4" data-id="element-315" /> 保存
          </Button>
          <Button onClick={handleSaveAndTest} disabled={!valid} data-id="element-316">
            <FlaskConicalIcon className="h-4 w-4" data-id="element-317" /> 保存并测试
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-id="element-318">
        <div className="space-y-4 lg:col-span-2" data-id="element-319">
          {/* Basic info */}
          <Card className="p-5" data-id="element-320">
            <h3 className="mb-4 text-sm font-semibold text-slate-200" data-id="element-321">
              基本信息
            </h3>
            <div className="space-y-4" data-id="element-322">
              <div data-id="element-323">
                <Label required data-id="element-324">Agent 名称</Label>
                <Input value={draft.name} onChange={e => set({
                name: e.target.value
              })} placeholder="例如 代码开发助手" data-id="element-325" />
              </div>
              <div data-id="element-326">
                <Label required data-id="element-327">简介</Label>
                <Input value={draft.description} onChange={e => set({
                description: e.target.value
              })} placeholder="展示在 Agent 列表卡片中" data-id="element-328" />
              </div>
              <div data-id="element-329">
                <Label data-id="element-330">标签</Label>
                <div className="flex flex-wrap items-center gap-1.5" data-id="element-331">
                  {draft.tags.map(t => <Badge key={t} tone="slate" data-id="element-332">
                      {t}
                      <button onClick={() => set({
                    tags: draft.tags.filter(x => x !== t)
                  })} className="ml-1 text-slate-500 hover:text-slate-300" data-id="element-333">
                        ×
                      </button>
                    </Badge>)}
                  <input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTag();
                  }
                }} placeholder="输入后回车" className="bg-transparent text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none" data-id="element-334" />
                </div>
              </div>
            </div>
          </Card>

          {/* Prompt editor */}
          <Card className="p-5" data-id="element-335">
            <div className="mb-3 flex items-center justify-between" data-id="element-336">
              <h3 className="text-sm font-semibold text-slate-200" data-id="element-337">
                系统提示词
              </h3>
              <div className="flex gap-1.5" data-id="element-338">
                {PROMPT_TEMPLATES.map(t => <button key={t.label} onClick={() => set({
                systemPrompt: t.text
              })} className="rounded-md border border-slate-700 px-2 py-0.5 text-xs text-slate-400 hover:bg-slate-800" data-id="element-339">
                    {t.label}
                  </button>)}
              </div>
            </div>
            <Textarea rows={6} value={draft.systemPrompt} onChange={e => set({
            systemPrompt: e.target.value
          })} placeholder="定义 Agent 的角色、边界和工作方式" className="font-mono text-xs" data-id="element-340" />
            <p className="mt-2 text-xs text-slate-500" data-id="element-341">
              提示：可使用 {'{{variable}}'} 形式引用运行时变量。
            </p>
          </Card>

          {/* Skill selector */}
          <Card className="p-5" data-id="element-342">
            <h3 className="mb-1 text-sm font-semibold text-slate-200" data-id="element-343">
              Skills
            </h3>
            <p className="mb-3 text-xs text-slate-500" data-id="element-344">
              从已安装且启用的 Skills 中多选
            </p>
            {skills.length === 0 ? <p className="text-sm text-slate-500" data-id="element-345">
                暂无已启用的 Skill，请先在 Skills 中心安装并启用。
              </p> : <div className="space-y-2" data-id="element-346">
                {skills.map(s => {
              const active = draft.skillIds.includes(s.skillId);
              return <button key={s.skillId} onClick={() => toggleSkill(s.skillId)} className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors ${active ? 'border-indigo-500/40 bg-indigo-500/10' : 'border-slate-800 hover:bg-slate-800/40'}`} data-id="element-347">
                      <div className={`flex h-8 w-8 items-center justify-center rounded-md ${active ? 'bg-indigo-600/30' : 'bg-slate-800'}`} data-id="element-348">
                        <PuzzleIcon className="h-4 w-4 text-purple-400" data-id="element-349" />
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-350">
                        <div className="flex items-center gap-2" data-id="element-351">
                          <span className="text-sm font-medium text-slate-200" data-id="element-352">
                            {s.name}
                          </span>
                          <span className="font-mono text-xs text-slate-500" data-id="element-353">
                            v{s.version}
                          </span>
                        </div>
                        <div className="truncate text-xs text-slate-500" data-id="element-354">
                          {s.description}
                        </div>
                      </div>
                      {active && <CheckCircle2Icon className="h-4 w-4 text-indigo-400" data-id="element-355" />}
                    </button>;
            })}
              </div>}
          </Card>

          {/* Knowledge base selector */}
          <Card className="p-5" data-id="element-356">
            <h3 className="mb-1 text-sm font-semibold text-slate-200" data-id="element-357">
              知识库
            </h3>
            <p className="mb-3 text-xs text-slate-500" data-id="element-358">
              从 ready 状态的知识库中多选，用于注入 Agent 上下文
            </p>
            {knowledgeBases.length === 0 ? <p className="text-sm text-slate-500" data-id="element-359">
                暂无 ready 状态的知识库，请先在知识库中心创建并完成索引。
              </p> : <div className="space-y-2" data-id="element-360">
                {knowledgeBases.map(kb => {
              const active = draft.knowledgeBaseIds.includes(kb.knowledgeBaseId);
              return <button key={kb.knowledgeBaseId} onClick={() => toggleKb(kb.knowledgeBaseId)} className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors ${active ? 'border-indigo-500/40 bg-indigo-500/10' : 'border-slate-800 hover:bg-slate-800/40'}`} data-id="element-361">
                      <div className={`flex h-8 w-8 items-center justify-center rounded-md ${active ? 'bg-indigo-600/30' : 'bg-slate-800'}`} data-id="element-362">
                        <BookOpenIcon className="h-4 w-4 text-emerald-400" data-id="element-363" />
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-364">
                        <div className="flex items-center gap-2" data-id="element-365">
                          <span className="text-sm font-medium text-slate-200" data-id="element-366">
                            {kb.name}
                          </span>
                          <span className="text-xs text-slate-500" data-id="element-367">
                            {kb.documentCount} 文档 · {kb.chunkCount} chunks
                          </span>
                        </div>
                        <div className="truncate text-xs text-slate-500" data-id="element-368">
                          {kb.description}
                        </div>
                      </div>
                      {active && <CheckCircle2Icon className="h-4 w-4 text-indigo-400" data-id="element-369" />}
                    </button>;
            })}
              </div>}
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-4" data-id="element-370">
          <Card className="p-5" data-id="element-371">
            <h3 className="mb-3 text-sm font-semibold text-slate-200" data-id="element-372">
              默认模型 <span className="text-red-400" data-id="element-373">*</span>
            </h3>
            {models.length === 0 ? <p className="text-sm text-slate-500" data-id="element-374">
                暂无启用模型，请先在模型中心配置。
              </p> : <Select value={draft.defaultModelId} onChange={e => set({
            defaultModelId: e.target.value
          })} data-id="element-375">
                {models.map(m => <option key={m.modelId} value={m.modelId} data-id="element-376">
                    {m.name}
                  </option>)}
              </Select>}
          </Card>

          {/* Retrieval config */}
          <Card className="p-5" data-id="element-377">
            <h3 className="mb-1 text-sm font-semibold text-slate-200" data-id="element-378">
              检索参数
            </h3>
            <p className="mb-3 text-xs text-slate-500" data-id="element-379">
              控制知识库检索行为（绑定知识库时生效）
            </p>
            <div className="space-y-4" data-id="element-380">
              <div data-id="element-381">
                <Label data-id="element-382">topK</Label>
                <Input type="number" value={draft.retrievalConfig?.topK ?? 5} onChange={e => set({
                retrievalConfig: {
                  topK: Number(e.target.value),
                  scoreThreshold: draft.retrievalConfig?.scoreThreshold ?? 0.7,
                  showCitations: draft.retrievalConfig?.showCitations ?? true
                }
              })} data-id="element-383" />
              </div>
              <div data-id="element-384">
                <Label data-id="element-385">scoreThreshold</Label>
                <Input type="number" step="0.05" value={draft.retrievalConfig?.scoreThreshold ?? 0.7} onChange={e => set({
                retrievalConfig: {
                  topK: draft.retrievalConfig?.topK ?? 5,
                  scoreThreshold: Number(e.target.value),
                  showCitations: draft.retrievalConfig?.showCitations ?? true
                }
              })} data-id="element-386" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-300" data-id="element-387">
                <input type="checkbox" checked={draft.retrievalConfig?.showCitations ?? true} onChange={e => set({
                retrievalConfig: {
                  topK: draft.retrievalConfig?.topK ?? 5,
                  scoreThreshold: draft.retrievalConfig?.scoreThreshold ?? 0.7,
                  showCitations: e.target.checked
                }
              })} className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-indigo-500" data-id="element-388" />
                回答中显示引用来源
              </label>
            </div>
          </Card>

          <Card className="p-5" data-id="element-389">
            <h3 className="mb-3 text-sm font-semibold text-slate-200" data-id="element-390">
              运行时配置
            </h3>
            <div className="space-y-4" data-id="element-391">
              <div data-id="element-392">
                <Label data-id="element-393">工具审批模式</Label>
                <Select value={draft.runtimeConfig.toolApprovalMode} onChange={e => set({
                runtimeConfig: {
                  ...draft.runtimeConfig,
                  toolApprovalMode: e.target.value as ToolApprovalMode
                }
              })} data-id="element-394">
                  <option value="auto" data-id="element-395">auto · 自动批准</option>
                  <option value="manual" data-id="element-396">manual · 手动确认</option>
                  <option value="restricted" data-id="element-397">restricted · 受限</option>
                </Select>
              </div>
              <div data-id="element-398">
                <Label data-id="element-399">最大执行时间 (秒)</Label>
                <Input type="number" value={draft.runtimeConfig.maxExecutionSeconds ?? ''} onChange={e => set({
                runtimeConfig: {
                  ...draft.runtimeConfig,
                  maxExecutionSeconds: Number(e.target.value)
                }
              })} data-id="element-400" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-300" data-id="element-401">
                <input type="checkbox" checked={!!draft.runtimeConfig.allowFileAccess} onChange={e => set({
                runtimeConfig: {
                  ...draft.runtimeConfig,
                  allowFileAccess: e.target.checked
                }
              })} className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-indigo-500" data-id="element-402" />
                允许文件访问
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-300" data-id="element-403">
                <input type="checkbox" checked={!!draft.runtimeConfig.allowNetworkAccess} onChange={e => set({
                runtimeConfig: {
                  ...draft.runtimeConfig,
                  allowNetworkAccess: e.target.checked
                }
              })} className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-indigo-500" data-id="element-404" />
                允许网络访问
              </label>
            </div>
          </Card>

          {savedAgent && <Card className="p-5" data-id="element-405">
              <h3 className="mb-2 text-sm font-semibold text-slate-200" data-id="element-406">
                测试
              </h3>
              <p className="mb-3 text-xs text-slate-500" data-id="element-407">
                测试不会污染正式历史 (is_test=true)。测试通过后可将 Agent
                设为已启用。
              </p>
              <div className="flex flex-col gap-2" data-id="element-408">
                <Button variant="outline" onClick={() => setTestOpen(true)} data-id="element-409">
                  <FlaskConicalIcon className="h-4 w-4" data-id="element-410" /> 打开测试对话
                </Button>
                {savedAgent.status !== 'active' && <Button onClick={() => {
              activateAgent(savedAgent.agentId);
              pushToast('success', 'Agent 已设为已启用');
              setSavedAgent({
                ...savedAgent,
                status: 'active'
              });
            }} data-id="element-411">
                    <CheckCircle2Icon className="h-4 w-4" data-id="element-412" /> 设为已启用
                  </Button>}
              </div>
            </Card>}
        </div>
      </div>

      {savedAgent && testOpen && <AgentChatDialog agent={savedAgent} open={testOpen} onClose={() => setTestOpen(false)} isTest data-id="element-413" />}
    </PageContainer>;
}